/**
 * Centralized query key factories for React Query
 * All hooks and prefetch utilities should use these keys for consistency and deduplication
 */

// Company keys
export const companyKeys = {
  all: ['company'] as const,
  info: () => [...companyKeys.all, 'info'] as const,
  settings: () => [...companyKeys.all, 'settings'] as const,
};

// Auth/session keys
export const authKeys = {
  all: ['auth'] as const,
  session: () => [...authKeys.all, 'session'] as const,
};

// Bank account keys
export const bankKeys = {
  all: ['bank-accounts'] as const,
  list: () => [...bankKeys.all, 'list'] as const,
  listWithTraining: () => [...bankKeys.all, 'list-with-training'] as const,
  detail: (accountId: string) => [...bankKeys.all, 'detail', accountId] as const,
  transactions: (accountId: string) => [...bankKeys.all, 'transactions', accountId] as const,
};

// Dashboard keys (insights key includes date range when provided for correct refetch on period change)
export const dashboardKeys = {
  all: ['dashboard'] as const,
  insights: (startDate?: string | null, endDate?: string | null) =>
    [...dashboardKeys.all, 'insights', startDate ?? '', endDate ?? ''] as const,
  kpi: () => [...dashboardKeys.all, 'kpi'] as const,
};

// Vendor keys
export const vendorKeys = {
  all: ['vendors'] as const,
  list: () => [...vendorKeys.all, 'list'] as const,
  detail: (vendorId: string) => [...vendorKeys.all, 'detail', vendorId] as const,
};

// Menu keys
export const menuKeys = {
  all: ['menu'] as const,
  items: () => [...menuKeys.all, 'items'] as const,
  itemDetail: (itemId: string) => [...menuKeys.all, 'item', itemId] as const,
  categories: () => [...menuKeys.all, 'categories'] as const,
};

// Ingredient keys
export const ingredientKeys = {
  all: ['ingredients'] as const,
  list: () => [...ingredientKeys.all, 'list'] as const,
  detail: (ingredientId: string) => [...ingredientKeys.all, 'detail', ingredientId] as const,
};

// Prep item keys
export const prepItemKeys = {
  all: ['prep-items'] as const,
  list: () => [...prepItemKeys.all, 'list'] as const,
  detail: (prepItemId: string) => [...prepItemKeys.all, 'detail', prepItemId] as const,
};

// Integration keys
export const integrationKeys = {
  all: ['integrations'] as const,
  list: () => [...integrationKeys.all, 'list'] as const,
};

// Employee keys
export const employeeKeys = {
  all: ['employees'] as const,
  list: () => [...employeeKeys.all, 'list'] as const,
};

// Contractor keys
export const contractorKeys = {
  all: ['contractors'] as const,
  list: () => [...contractorKeys.all, 'list'] as const,
};

// Shareholder keys
export const shareholderKeys = {
  all: ['shareholders'] as const,
  list: () => [...shareholderKeys.all, 'list'] as const,
};

// Location keys
export const locationKeys = {
  all: ['locations'] as const,
  list: () => [...locationKeys.all, 'list'] as const,
};

// Tax keys
export const taxKeys = {
  all: ['tax'] as const,
  forms: () => [...taxKeys.all, 'forms'] as const,
  plCalculations: () => [...taxKeys.all, 'pl-calculations'] as const,
  dataAggregation: () => [...taxKeys.all, 'data-aggregation'] as const,
};

// P&L and reporting keys
export const reportingKeys = {
  all: ['reporting'] as const,
  plCalculations: () => [...reportingKeys.all, 'pl-calculations'] as const,
  realPLCalculations: () => [...reportingKeys.all, 'real-pl-calculations'] as const,
  cashFlow: () => [...reportingKeys.all, 'cash-flow'] as const,
  balanceSheet: () => [...reportingKeys.all, 'balance-sheet'] as const,
  comparison: () => [...reportingKeys.all, 'comparison'] as const,
};

// Notification keys
export const notificationKeys = {
  all: ['notifications'] as const,
  data: () => [...notificationKeys.all, 'data'] as const,
  payrollUnassigned: () => [...notificationKeys.all, 'payroll-unassigned'] as const,
};

// Onboarding keys
export const onboardingKeys = {
  all: ['onboarding'] as const,
  complete: () => [...onboardingKeys.all, 'complete'] as const,
  progress: () => [...onboardingKeys.all, 'progress'] as const,
};

// Shift performance keys
export const shiftPerformanceKeys = {
  all: ['shift-performance'] as const,
  list: (companyId: string, startDate: string, endDate: string) =>
    [...shiftPerformanceKeys.all, 'list', companyId, startDate, endDate] as const,
};

// Asset keys
export const assetKeys = {
  all: ['assets'] as const,
  sales: () => [...assetKeys.all, 'sales'] as const,
};

// Chart data keys
export const chartKeys = {
  all: ['charts'] as const,
  data: () => [...chartKeys.all, 'data'] as const,
};

// Expense keys (expenses table + bank_statements + bank_transactions)
export const expenseKeys = {
  all: ['expenses'] as const,
  list: (companyId: string) => [...expenseKeys.all, 'list', companyId] as const,
  filtered: (
    companyId: string,
    dateFrom: string,
    dateTo: string,
    categoryFilter: string
  ) =>
    [...expenseKeys.all, 'filtered', companyId, dateFrom, dateTo, categoryFilter] as const,
  categories: (companyId: string) => [...expenseKeys.all, 'categories', companyId] as const,
};

/**
 * Query options with recommended staleTime and gcTime per key type
 * Use these when you need consistent cache behavior across hooks and prefetch
 */
export const queryOptions = {
  company: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  },
  auth: {
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
  },
  bankAccounts: {
    staleTime: 2 * 60 * 1000, // 2 minutes (more frequently updated)
    gcTime: 10 * 60 * 1000,
  },
  dashboard: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000,
  },
  vendors: {
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  },
  menu: {
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  },
  ingredients: {
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  },
  prepItems: {
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  },
  integrations: {
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  },
  reporting: {
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  },
  expenses: {
    staleTime: 2 * 60 * 1000, // 2 minutes (frequently updated with imports)
    gcTime: 10 * 60 * 1000,
  },
  onboarding: {
    staleTime: 30 * 1000, // 30 seconds so dashboard status card updates after completing modules
    gcTime: 5 * 60 * 1000,
  },
  shiftPerformance: {
    staleTime: 5 * 60 * 1000, // 5 minutes (updated on ingestion)
    gcTime: 10 * 60 * 1000,
  },
} as const;
